import React, { useEffect, useState, useMemo, useCallback } from 'react'; //, { useState }
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { debounce } from 'lodash';

import { useParams } from 'react-router-dom';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Button from '@material-ui/core/Button';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';

import compose from 'helpers/compose';
import Tooltip from 'components/Tooltip/Tooltip';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { RootStore } from 'stores/rootStore';
import { EdwardColors } from 'enums';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertFromRaw } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import translations from './EditorCS.js';

import { API_ACCOUNT_NOTE } from 'api/endpoints';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      position: 'relative',
      minHeight: '100%',
      maxHeight: '100%',
      height: '100%',
      background: EdwardColors.LIGHTER,
      padding: theme.spacing(1, 1, 1, 1),
    },
    title: {
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      position: 'relative',
      paddingRight: theme.spacing(6),
      height: theme.spacing(6),
      lineHeight: `${theme.spacing(6)}px`,
      marginBottom: theme.spacing(1),
      '& button': {
        position: 'absolute',
        right: 0,
        top: 0,
      },
    },
    actionBar: {
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      flex: '0 0 auto',
      paddingTop: theme.spacing(1),
    },
    saveAndExit: {},
    editorWrapper: {
      maxHeight: 'calc(100% - 108px)',
      height: '100%',
      position: 'relative',

      '& .rdw-editor-wrapper': {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
        height: '100%',
      },
      '& .rdw-editor-toolbar': {
        background: EdwardColors.DARK,
        border: 'none',
        flex: '0 0 auto',
      },
      '& .rdw-editor-main': {
        background: EdwardColors.LIGHT,
        padding: theme.spacing(1, 1, 1, 1),
        flex: '1 1 auto',
        overflowY: 'auto',
        height: 'auto',
      },
      '& .rdw-option-wrapper, & .rdw-dropdown-wrapper': {
        borderColor: EdwardColors.MEDIUM,
        position: 'relative',
        '&:after': {
          content: '""',
          display: 'block',
          width: '100%',
          height: '100%',
          position: 'absolute',
          left: 0,
          top: 0,
          background: 'rgba(255, 203, 19, 0.33)',
          pointerEvents: 'none',
        },
        '&.rdw-option-active:after': {
          background: 'rgba(255, 203, 19, 0)',
        },
      },
      '& .rdw-option-active': {
        boxShadow: 'none',
      },
      '& .rdw-colorpicker-modal, & .rdw-link-modal': {
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%,-50%)',
      },
    },
    logout: {
      textAlign: 'center',
      padding: theme.spacing(4, 0),
    },
    extension: {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'flex-end',
      alignItems: 'center',
      paddingRight: theme.spacing(2),
    },
  }),
);

interface Note {
  accountId: string;
  variableSymbol: string;
  title: string;
  note: string;
  isProtected?: boolean;
}

interface Props {
  rootStore: RootStore;
}
interface ExtensionProps {
  saved: boolean | null;
}

const Extension: React.FC<ExtensionProps> = ({ saved }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <div className={classes.extension}>
      {saved === null ? '' : <>{saved ? t('accountNotes_notes_saved') : <LoadingIndicator color="secondary" />}</>}
    </div>
  );
};

const Notes: React.FC<Props> = ({ rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [logout, setLogout] = useState(false);
  const [state, setState] = useState(); //
  const [title, setTitle] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [saved, setSaved] = useState<null | boolean>(true);
  const [deleted, setDeleted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [readOnly, setReadOnly] = useState(true);

  const { accountId } = useParams<Params>();

  const handleSave = useCallback(
    async (text: string) => {
      if (!readOnly) {
        setSaved(false);
        await rootStore.stores.apiStore.post(API_ACCOUNT_NOTE(accountId), { note: JSON.stringify(text) });
        setSaved(true);
      }
    },
    [accountId, rootStore.stores.apiStore, readOnly],
  );

  const handleDelete = useCallback(async () => {
    await rootStore.stores.apiStore.delete(API_ACCOUNT_NOTE(accountId));
    window.opener &&
      window.opener.postMessage({ call: 'deleteNote', value: { accountId: accountId } }, window.location.origin);
    setDeleted(true);
    window.self.close();
  }, [accountId, rootStore.stores.apiStore]);

  useEffect(() => {
    (async () => {
      const response = await rootStore.stores.apiStore.get<Note>(API_ACCOUNT_NOTE(accountId));
      if (response?.data) {
        const note = response.data.note ? JSON.parse(response.data.note) : undefined;
        setState(note ? EditorState.createWithContent(convertFromRaw(note)) : EditorState.createEmpty());
        setTitle(response.data.title || '');
        setReadOnly(!!response.data.isProtected);
        window.opener &&
          window.opener.postMessage({ call: 'addNote', value: { accountId: accountId } }, window.location.origin);
      }
    })();
  }, [accountId, rootStore.stores.apiStore]);

  const handleSaveDebounced = useMemo(
    () =>
      debounce(async (text: string) => {
        await handleSave(text);
      }, 3000),
    [handleSave],
  );

  useEffect(() => {
    rootStore.stores.uiStore.setWindowTitle(`${t('accountNotes_title')} - ${accountId}`);
    const handleEvents = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data) {
        if (event.data.call === 'logout') {
          setLogout(true);
        }
        if (event.data.call === 'refresh') {
          rootStore.stores.userStore.resetSessionTimers();
        }
        if (event.data.call === 'titleNote' && event.data.value) {
          setTitle(event.data.value);
        }
      }
    };

    window.addEventListener('message', handleEvents);

    window.onbeforeunload = () => {
      window.opener && window.opener.postMessage({ call: 'closeNote' }, window.location.origin);
      if (!saved && !deleted) return '???';
    };

    return () => {
      handleSaveDebounced.cancel();
    };
  }, [rootStore.stores.uiStore, rootStore.stores.userStore, t, accountId, handleSaveDebounced, saved, deleted]);

  const updateState = (state: any) => {
    setState(state);
  };

  const updateText = (text: any) => {
    setText(text);
    rootStore.stores.userStore.resetSessionTimers();
    window.opener && window.opener.postMessage({ call: 'refresh' }, window.location.origin);
    setSaved(null);
    handleSaveDebounced(text);
  };

  const handleSaveAndExit = async () => {
    if (!saved) {
      handleSaveDebounced.cancel();
      rootStore.stores.uiStore.setLoading(true);
      await handleSave(text);
      rootStore.stores.uiStore.setLoading(false);
    }
    window.self.close();
  };

  return title !== null ? (
    <div className={classes.root}>
      {logout ? (
        <Typography variant="h1" className={classes.logout}>
          {t('accountNotes_notes_loggedOut')}
        </Typography>
      ) : (
        <>
          <Typography variant="h1" className={classes.title}>
            {t('accountNotes_notes')}: {title}
            {!readOnly && (
              <Tooltip title={t('accountNotes_notes_delete')} placement="top">
                <IconButton size="medium" onClick={() => setDialogOpen(true)}>
                  <DeleteIcon fontSize="inherit" htmlColor={EdwardColors.SECONDARY} />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
          <div className={classes.editorWrapper}>
            <Editor
              toolbarCustomButtons={[<Extension saved={saved} />]}
              onEditorStateChange={readOnly ? undefined : updateState}
              onContentStateChange={readOnly ? undefined : updateText}
              editorState={state}
              toolbarHidden={readOnly}
              toolbar={{
                options: ['inline', 'blockType', 'list', 'textAlign', 'colorPicker', 'link', 'remove', 'history'],
                inline: {
                  options: ['bold', 'italic', 'underline', 'strikethrough'],
                },
                list: {
                  options: ['unordered', 'ordered'],
                },
                blockType: {
                  options: ['Normal', 'H1', 'H2', 'H3'],
                },
              }}
              localization={{
                locale: 'cs',
                translations,
              }}
            />
            <div className={classes.actionBar}>
              <Button
                onClick={handleSaveAndExit}
                color="primary"
                variant="contained"
                endIcon={<ArrowForwardIos />}
                className={classes.saveAndExit}
              >
                {t(readOnly ? 'general_close' : 'general_saveAndClose')}
              </Button>
            </div>
          </div>
          <ConfirmDialog
            isOpen={dialogOpen}
            close={() => {
              setDialogOpen(false);
            }}
            confirm={handleDelete}
            noHeader
          >
            {t('accountNotes_notes_delete_confirm')}
          </ConfirmDialog>
        </>
      )}
    </div>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Notes);
